import { useMemo, useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from 'recharts';

type Segment = 'standard' | 'mini' | 'micro';

const SEGMENTS: { id: Segment; label: string }[] = [
  { id: 'standard', label: 'Standard Lot' },
  { id: 'mini', label: 'Mini Lot' },
  { id: 'micro', label: 'Micro Lot' },
];

const MULTIPLIERS: Record<Segment, number> = {
  standard: 1,
  mini: 0.1,
  micro: 0.01,
};

export default function TerminalHero() {
  const [segment, setSegment] = useState<Segment>('standard');
  const [risk, setRisk] = useState(1.5);

  const lots = useMemo(() => {
    const balance = 10000;
    const stopLossPips = 10;
    const pipValue = 10;
    const mult = MULTIPLIERS[segment];
    return (balance * (risk / 100)) / (stopLossPips * pipValue * mult);
  }, [risk, segment]);

  const data = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => {
        const base = 30 + i * 2;
        const wave = Math.sin(i * 0.7) * 6 * (risk / 1.5);
        const trend = Math.pow(i / 13, 1.6) * risk * 42;
        return { x: i, value: Number((base + wave + trend).toFixed(2)) };
      }),
    [risk],
  );

  const fillPct = ((risk - 0.5) / (5 - 0.5)) * 100;

  return (
    <section className="relative min-h-[90vh] w-full flex items-center overflow-hidden bg-gradient-to-b from-white to-[#F8FAFC] px-4 md:px-6 pt-28 pb-16">
      <div className="mx-auto grid w-full max-w-[1180px] items-center gap-12 lg:grid-cols-2">
        {/* Left: text */}
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-violet-100/70 px-4 py-1.5 text-xs font-semibold text-violet-700">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-600"></span>
            Trading Terminal thời gian thực
          </span>
          <h1 className="mt-6 font-display text-4xl font-bold leading-[1.1] text-slate-900 md:text-5xl">
            Định hình Chiến lược giao dịch bằng{' '}
            <span className="text-violet-600">Dữ liệu tuyệt đối.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
            Không còn những phỏng đoán. Đây là trung tâm công cụ Forex và máy tính
            Forex thời gian thực của bạn. Lượng hóa mọi rủi ro và tối ưu hóa chiến
            lược giao dịch ngay trên giao diện này.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href="#scanner"
              className="whitespace-nowrap rounded-full bg-violet-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-violet-700"
            >
              Khám phá công cụ
            </a>
            <a
              href="#tools"
              className="whitespace-nowrap rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-violet-300 hover:text-violet-700"
            >
              Xem thị trường
            </a>
          </div>
        </div>

        {/* Right: live position size tool */}
        <div className="animate-float">
          <div className="rounded-3xl frosted-strong border border-white/70 p-6 shadow-[0_30px_80px_-30px_rgba(124,58,237,0.35)]">
            {/* Window header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-300"></span>
                <span className="w-3 h-3 rounded-full bg-amber-300"></span>
                <span className="w-3 h-3 rounded-full bg-emerald-300"></span>
              </div>
              <span className="font-mono text-xs text-slate-400">Position Size</span>
            </div>

            {/* Segmented control */}
            <div className="mt-5 flex rounded-full bg-slate-100 p-1">
              {SEGMENTS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSegment(s.id)}
                  className={`flex-1 whitespace-nowrap rounded-full px-3 py-2 text-xs font-semibold transition-all ${
                    segment === s.id
                      ? 'bg-white text-violet-700 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {/* Risk slider */}
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-600">% Rủi ro (Risk)</span>
                <span className="font-mono text-sm font-semibold text-violet-600">
                  {risk.toFixed(1)}%
                </span>
              </div>
              <input
                type="range"
                min={0.5}
                max={5}
                step={0.1}
                value={risk}
                onChange={(e) => setRisk(Number(e.target.value))}
                className="mt-3"
                style={{ '--fill': `${fillPct}%` } as React.CSSProperties}
              />
              <div className="mt-1 flex justify-between text-[11px] text-slate-400">
                <span>0.5%</span>
                <span>5%</span>
              </div>
            </div>

            {/* Area chart */}
            <div className="mt-4 h-32 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="riskArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#7C3AED" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="x" hide />
                  <YAxis hide domain={['dataMin', 'dataMax']} />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#7C3AED"
                    strokeWidth={2.5}
                    fill="url(#riskArea)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Result */}
            <div className="mt-4 flex items-end justify-between border-t border-slate-100 pt-4">
              <div>
                <p className="text-xs text-slate-400">Khối lượng đề xuất</p>
                <p className="mt-1 font-mono text-[10px] text-slate-400">
                  Số dư $10,000 · SL 10 pip
                </p>
              </div>
              <div className="text-right">
                <span className="font-display text-4xl font-bold text-slate-900 md:text-5xl">
                  {lots.toFixed(2)}
                </span>
                <span className="ml-2 text-sm font-semibold text-violet-600">Lô</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}